// Pure helper functions for the Place Selection Builder ("Option C — Hybrid")
// Places-step experience.
//
// These are intentionally free of any Vue/component or mapbox-gl dependency so
// that fast-check property tests can import and exercise them directly. They
// mirror the extraction pattern used by placeSelectionDrawHelpers.js.
//
// Feature: place-selection-builder (Task 1)
//
// The requirements vocabulary (Source_Type: `searched` / `drawn` / `uploaded`)
// is mapped to the code's `_source` vocabulary (`library` / `draw` / `upload` /
// `csv`) in exactly one place — `sourceType` — so the two stay consistent.

/**
 * Map a Place_Item's `_source` to its Source_Type.
 *
 * The mapping is total over the code's `_source` values:
 *   - `library`        → `searched`
 *   - `draw`           → `drawn`
 *   - `upload` / `csv` → `uploaded`
 *
 * @param {Object} place - The Place_Item (expects a `_source` field).
 * @returns {'searched' | 'drawn' | 'uploaded' | null} The Source_Type, or null
 *   when the `_source` cannot be classified.
 */
export function sourceType(place) {
  const source = place && place._source;
  switch (source) {
    case 'library':
      return 'searched';
    case 'draw':
      return 'drawn';
    case 'upload':
    case 'csv':
      return 'uploaded';
    default:
      return null;
  }
}

/**
 * Compute the stable Place_Key used to de-duplicate a Place_Item.
 *
 * - Searched places (Source_Type `searched`) key on `near_poi_id`.
 * - Drawn and uploaded places key on their generated `_id`.
 *
 * Returns `null` when the required key is absent or empty (empty string,
 * whitespace-only, null, or undefined), so callers can exclude the pick.
 *
 * @param {Object} place - The Place_Item.
 * @returns {string | null} The Place_Key, or null when the required key is
 *   missing/empty.
 */
export function placeKey(place) {
  if (!place) return null;
  const type = sourceType(place);
  if (type === null) return null;
  const raw = type === 'searched' ? place.near_poi_id : place._id;
  if (raw == null) return null;
  const key = String(raw).trim();
  return key === '' ? null : key;
}

/**
 * Determine whether a Place_Item has a usable location that the Builder_Map can
 * focus on.
 *
 * True when the item carries a geometry, or (for csv uploads that have no
 * top-level geometry) at least one `previewFeatures` Point entry. This mirrors
 * the existing `isZoomable` logic in PlaceSelectionReview.vue so the
 * Zoom_Affordance presence and map focusability stay in lockstep.
 *
 * @param {Object} place - The Place_Item.
 * @returns {boolean} True when a usable geometry or a csv preview Point exists.
 */
export function hasResolvableLocation(place) {
  if (!place) return false;
  if (place.geometry) return true;
  const features = place.previewFeatures;
  if (!Array.isArray(features)) return false;
  return features.some((feature) => {
    const geometry = feature && feature.geometry;
    return !!geometry && geometry.type === 'Point';
  });
}

/**
 * Return a new list that excludes exactly the item whose `_id` matches `id`,
 * preserving the order of all remaining items.
 *
 * Mirrors the store's `removeSelectedPlace` filter (remove by `_id`) as a pure
 * function so it can be property-tested. The input list is never mutated.
 *
 * @param {Array<Object>} list - The Place_List.
 * @param {string} id - The `_id` of the item to remove.
 * @returns {Array<Object>} A new list with the matching item removed.
 */
export function removeByKey(list, id) {
  const places = Array.isArray(list) ? list : [];
  return places.filter((place) => !(place && place._id === id));
}

/**
 * Merge newly confirmed picks into the existing Place_List, de-duplicating by
 * Place_Key, preserving the existing items and their order, and capping the
 * result length.
 *
 * Contract (see design.md "mergeSelection(existing, picked, { max }) contract"):
 *   1. Start from `existing` unchanged, preserving order.
 *   2. Build a set of existing Place_Keys (via `placeKey`).
 *   3. Iterate `picked` in selection order; for each pick compute
 *      `key = placeKey(p)`, `name = p.name`, `type = sourceType(p)`.
 *      - Skip and record in `skipped` any pick missing a Place_Key, a display
 *        name (missing/empty/whitespace-only), or a determinable Source_Type
 *        (`type === null`).
 *      - Discard picks whose Place_Key already exists (in `existing` or the
 *        already-added set), keeping the first occurrence and existing position.
 *   4. Enforce the cap: never let `items.length` exceed `max`; once at the cap,
 *      further picks are not added.
 *   5. Empty `picked` → return `{ items: existing, skipped: [] }` (no-op — the
 *      existing reference is returned unchanged).
 *
 * The input arrays are never mutated.
 *
 * @param {Array<Object>} existing - The current Place_List.
 * @param {Array<Object>} picked - The newly confirmed picks, in selection order.
 * @param {Object} [options] - Options.
 * @param {number} [options.max=1000] - The maximum resulting list size.
 * @returns {{ items: Array<Object>, skipped: Array<Object> }} The resulting
 *   full list and the picks that could not be added.
 */
export function mergeSelection(existing, picked, { max = 1000 } = {}) {
  const existingList = Array.isArray(existing) ? existing : [];
  const picks = Array.isArray(picked) ? picked : [];

  // Empty confirmation is a no-op: return the existing reference unchanged.
  if (picks.length === 0) {
    return { items: existingList, skipped: [] };
  }

  const items = existingList.slice();
  const skipped = [];

  const seenKeys = new Set();
  existingList.forEach((place) => {
    const key = placeKey(place);
    if (key !== null) seenKeys.add(key);
  });

  picks.forEach((pick) => {
    const key = placeKey(pick);
    const type = sourceType(pick);
    const name = pick && pick.name;
    const hasName = typeof name === 'string' && name.trim() !== '';

    // Invalid picks: missing Place_Key, undeterminable Source_Type, or no name.
    if (key === null || type === null || !hasName) {
      skipped.push(pick);
      return;
    }

    // De-duplicate by Place_Key (keep first occurrence / existing position).
    if (seenKeys.has(key)) return;

    // Enforce the cap: never exceed `max` items.
    if (items.length >= max) return;

    items.push(pick);
    seenKeys.add(key);
  });

  return { items, skipped };
}
